/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 27-07-2026
 *
 * Component voor de knoppenkolom rechts van het modelcanvas: de vier feedbacklampjes
 * (simulatiefeedback, normfouten, noodzakelijke en gewenste bouwsuggesties) met hun tellers,
 * hover-ballonnen en "nieuwe dingen"-popups, plus de tekstbeschrijving-toggle.
 * Vervangt `#feedbackbalk` in canvas.html.
 *
 * De tellers komen rechtstreeks uit de ng21-engines, de toon-vlaggen uit `DlModel` en de
 * projectvlaggen uit de `ProjectVlaggenService`. Het normfout-overzicht is sinds golf 3b een
 * berekening in de `NormService` en de selectie leeft in de `FeedbackSelectieService`, die de klik
 * op een knopje meteen doorzet. De toggles voert de `FeedbackbalkService` uit (golf 3c); transport en
 * log lopen daarvandaan als callbacks naar de samenwerk-baas (tot golf 4f naar ng1).
 */
import {ChangeDetectionStrategy, Component, DestroyRef, HostListener, Input, computed, effect, inject, signal} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {KeyedTimeout} from 'sihw-ngx';
import type {NodeSingular} from 'cytoscape';
import {BaseIslandComponent} from '../base-island.component';
import {DlModel} from '../../app/model/dl-model';
import {GlobalModelcanvasService} from '../../app/model/global-modelcanvas.service';
import {BouwsuggestieEngineService} from '../../app/engines/bouwsuggestie-engine.service';
import {NormService} from '../../app/engines/norm.service';
import {SimfeedbackEngineService} from '../../app/engines/simfeedback-engine.service';
import {FeedbackbalkService} from '../../services/feedbackbalk.service';
import {FeedbackSelectieService} from '../../services/feedback-selectie.service';
import type {BouwsuggestieSoort} from '../../services/feedback-selectie.service';
import {ProjectVlaggenService} from '../../services/project-vlaggen.service';
import {SimulatieStatusService} from '../../services/simulatie-status.service';
import {WeergaveInstellingenService} from '../../services/weergave-instellingen.service';
import {DlMaxHoogteCanvasDirective} from '../../directives/dl-max-hoogte-canvas/dl-max-hoogte-canvas.directive';

/**
 * De vier feedbacksoorten van de balk, in de volgorde waarin ze in de kolom staan.
 */
type Soort = 'sim' | 'norm' | 'noodzakelijk' | 'gewenst';

/**
 * Eén regelgroep in de bouwsuggestie-ballon: de vertaalde titel met de nodes waarvoor hij geldt.
 * Per node gaat de regel-`handle` mee: twee regels die dezelfde titel opleveren vallen in één groep,
 * en dan verschilt de handle binnen de groep — hij is de sleutel voor de selectie en het actielog.
 */
interface BouwsuggestieGroep {
    titel: string;
    instanties: {id: string; handle: string}[];
}

/**
 * De vertraging waarmee een hover-ballon sluit, zodat de muis naar de ballon kan bewegen.
 */
const SLUITVERTRAGING = 150;

@Component({
    selector: 'dl-feedbackbalk',
    standalone: true,
    templateUrl: './dl-feedbackbalk.component.html',
    styleUrls: ['./dl-feedbackbalk.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslatePipe, DlMaxHoogteCanvasDirective]
})
export class DlFeedbackbalkComponent extends BaseIslandComponent {

    // --- Properties ---

    /**
     * Het modeldocument: de vier toon-vlaggen en het normdocument.
     */
    protected readonly dlModel = inject(DlModel);

    /**
     * De simulatiefeedback-engine: beschikbaarheid en de feedbackitems voor de ballon.
     */
    protected readonly simEngine = inject(SimfeedbackEngineService);

    /**
     * De bouwsuggestie-engine: de totalen voor de tellers en de regels per node voor de ballon.
     */
    private readonly bouwsuggestieEngine = inject(BouwsuggestieEngineService);

    /**
     * De norm-service: het aantal normfouten.
     */
    private readonly normService = inject(NormService);

    /**
     * De projectvlaggen: bepalen welke lampjes er überhaupt staan.
     */
    private readonly projectVlaggen = inject(ProjectVlaggenService);

    /**
     * Draait de simulatie? Bepaalt welke lampjes actief kunnen zijn.
     */
    private readonly simulatieStatus = inject(SimulatieStatusService);

    /**
     * De toggle-intenties en de init-lock.
     */
    private readonly feedbackbalk = inject(FeedbackbalkService);

    /**
     * De feedbackselectie: welk knopje aan staat, en waar een klik erop landt.
     */
    private readonly feedbackSelectie = inject(FeedbackSelectieService);

    /**
     * De weergave-instellingen, voor de tekstbeschrijving-toggle.
     */
    protected readonly weergaveInstellingen = inject(WeergaveInstellingenService);

    /**
     * De canvas-eigenaar: bron van de nodes voor het bouwsuggestie-overzicht.
     */
    private readonly modelcanvas = inject(GlobalModelcanvasService);

    private readonly translateService = inject(TranslateService);

    private readonly destroyRef = inject(DestroyRef);

    /**
     * De soort waarvan de hover-ballon open staat, of null.
     */
    protected readonly hover = signal<Soort | null>(null);

    /**
     * Of de muis zich in de hover-ballon zelf bevindt (sticky hover).
     */
    private readonly popupOver = signal(false);

    /**
     * De "nieuwe dingen"-popuptekst per soort; leeg betekent geen ballon.
     */
    protected readonly melding = signal<Record<Soort, string | null>>({sim: null, norm: null, noodzakelijk: null, gewenst: null});

    /**
     * Het bouwsuggestie-overzicht per soort, opgebouwd bij het openen van de ballon.
     */
    protected readonly bouwsuggestieOverzicht = signal<Record<BouwsuggestieSoort, BouwsuggestieGroep[]>>({noodzakelijk: [], gewenst: []});

    /**
     * Het aantal elementen op het canvas; bepaalt of de tekstbeschrijving-knop zin heeft.
     */
    private readonly aantalElementen = signal(0);

    /**
     * De sluit-timers van de hover-ballonnen.
     */
    private readonly hoverTimer = new KeyedTimeout();


    constructor() {
        super();
        this.feedbackbalk.registreerWeergave({
            reset: () => this.reset(),
            sluitPopupteksten: () => this.wisMeldingen()
        });
        this.volgCanvas();
        this.volgTellers();
    }

    // --- Inputs ---

    /**
     * Debugvlag `normmatch_debug`: toont de gebruikte fout- en vertaalsleutels in de ballonnen.
     */
    @Input()
    set debugNormmatch(waarde: boolean) {
        this._debugNormmatch.set(waarde);
        this.cdr.markForCheck();
    }
    protected readonly _debugNormmatch = signal(false);

    // --- Afgeleide state ---

    /**
     * Draait de simulatie?
     */
    protected readonly simuleren = this.simulatieStatus.simuleren;

    /**
     * Het aantal beschikbare simfeedback-items.
     */
    protected readonly simTelling = this.simEngine.numBeschikbaar;

    /**
     * Het aantal normfouten.
     */
    protected readonly normTelling = this.normService.aantalFouten;

    /**
     * Het aantal noodzakelijke bouwsuggesties over het hele model. Sinds golf 3c een computed van
     * de `FeedbackbalkService`, die hem ook als toggle-guard gebruikt.
     */
    protected readonly noodzakelijkTelling = this.feedbackbalk.noodzakelijkTelling;

    /**
     * Het aantal gewenste bouwsuggesties over het hele model.
     */
    protected readonly gewenstTelling = this.feedbackbalk.gewenstTelling;

    /**
     * Of de knop van deze soort in de balk staat.
     */
    protected readonly toontSim = computed(() => this.simEngine.engineAan());
    protected readonly toontNorm = computed(() => this.projectVlaggen.normFoutfeedback());
    protected readonly toontNoodzakelijk = computed(() => this.projectVlaggen.bouwsuggestiesNoodzakelijk());
    protected readonly toontGewenst = computed(() => this.projectVlaggen.bouwsuggestiesGewenst());

    /**
     * Of de tekstbeschrijving-knop zin heeft: het project ondersteunt hem en er staat iets op het canvas.
     */
    protected readonly toontTekstbeschrijving = computed(() => this.projectVlaggen.tekstbeschrijving() && this.aantalElementen() > 0);

    /**
     * Of er iets te melden valt bij deze soort: het lampje licht op.
     */
    protected readonly simBeschikbaar = computed(() => this.simuleren() && this.simTelling() > 0);
    protected readonly normBeschikbaar = computed(() => !this.simuleren() && this.normService.heeftFouten());
    protected readonly noodzakelijkBeschikbaar = computed(() => !this.simuleren() && this.noodzakelijkTelling() > 0);
    protected readonly gewenstBeschikbaar = computed(() => !this.simuleren() && this.gewenstTelling() > 0);

    /**
     * Of de feedback van deze soort aan staat: het lampje is vol zichtbaar en aanklikbaar.
     */
    protected readonly simActief = computed(() => this.simuleren() && this.dlModel.simToon);
    protected readonly normActief = computed(() => !this.simuleren() && this.dlModel.normToon);
    protected readonly noodzakelijkActief = computed(() => !this.simuleren() && this.dlModel.bsToonNoodzakelijk);
    protected readonly gewenstActief = computed(() => !this.simuleren() && this.dlModel.bsToonGewenst);

    /**
     * De actuele feedbackselectie.
     */
    protected readonly selectie = this.feedbackSelectie.selectie;

    /**
     * Het normfout-overzicht zoals de `NormService` het berekent.
     */
    protected readonly normOverzicht = this.normService.foutoverzicht;

    /**
     * De plaats van de geselecteerde normfout in het huidige overzicht, of `null`. De selectie zelf
     * gaat op sleutel (element + foutcode); de knopjes staan op positie, dus die vertaling maken we
     * hier één keer.
     */
    protected readonly geselecteerdeFout = computed(() => {
        const fout = this.selectie().fout;
        return fout ? this.normService.zoekFoutregel(fout.elementId, fout.foutcode) : null;
    });

    /**
     * De simfeedback-categorieën met hun loops, in de volgorde van de engine.
     */
    protected readonly simCategorieen = computed(() =>
        Object.entries(this.simEngine.feedback())
            .filter(([, loops]) => loops.length)
            .map(([type, loops]) => ({type, loops})));

    /**
     * Het normfout-overzicht met per regel zijn positie (waarop de selectie-vergelijking loopt) en
     * de elementen als genummerde knopjes.
     */
    protected readonly normRegels = computed(() => this.normOverzicht().map((regel, foutindex) => ({
        ...regel,
        foutindex,
        knopjes: regel.elementen.map((element, elementindex) => ({elementindex, elementId: element.el.id, foutcode: element.foutcode}))
    })));

    /* Zichtbaarheid van de ballonnen: een hover-ballon staat er alleen als er ook inhoud is. */

    protected readonly simBallon = computed(() => this.hover() === 'sim' && this.simuleren() && this.simTelling() > 0);
    protected readonly normBallon = computed(() => this.hover() === 'norm' && !this.simuleren() && this.normOverzicht().length > 0);
    protected readonly noodzakelijkBallon = computed(() => this.hover() === 'noodzakelijk' && !this.simuleren() && this.bouwsuggestieOverzicht().noodzakelijk.length > 0);
    protected readonly gewenstBallon = computed(() => this.hover() === 'gewenst' && !this.simuleren() && this.bouwsuggestieOverzicht().gewenst.length > 0);

    /* De "nieuwe dingen"-ballonnen staan er alleen als de bijbehorende feedback aan staat. */

    protected readonly simMelding = computed(() => (this.simActief() ? this.melding().sim : null));
    protected readonly normMelding = computed(() => (!this.simuleren() && this.dlModel.normToon ? this.melding().norm : null));
    protected readonly noodzakelijkMelding = computed(() => (this.noodzakelijkActief() ? this.melding().noodzakelijk : null));
    protected readonly gewenstMelding = computed(() => (this.gewenstActief() ? this.melding().gewenst : null));

    /*
     * De ruimtemaker: een ballon hangt normaal absoluut naast zijn knop, maar zodra er ook onder (of
     * bij 'gewenst': boven) een ballon open staat, gaat hij in de flow staan zodat ze elkaar niet
     * overlappen. Overgenomen uit de `onderPopupOpenBij*`-helpers van de ng1-versie.
     */

    protected readonly simRuimte = computed(() =>
        !!(this.normMelding() || this.noodzakelijkBallon() || this.noodzakelijkMelding() || this.gewenstBallon() || this.gewenstMelding()));
    protected readonly normRuimte = computed(() =>
        !!(this.noodzakelijkBallon() || this.noodzakelijkMelding() || this.gewenstBallon() || this.gewenstMelding()));
    protected readonly noodzakelijkRuimte = computed(() => !!(this.gewenstBallon() || this.gewenstMelding()));
    protected readonly gewenstRuimte = computed(() => !!(this.noodzakelijkBallon() || this.noodzakelijkMelding()));

    // --- Protected methods (template-acties) ---

    /**
     * De muis komt op een lampje: sluit wat open staat, ververs de inhoud en toon de ballon.
     */
    protected muisBinnen(soort: Soort): void {
        if (soort === 'sim' ? !this.simuleren() : this.simuleren()) {
            return; //simfeedback alleen tijdens simulatie, de rest juist alleen erbuiten
        }
        if (soort === 'sim' && this.simTelling() === 0) {
            return; //niets te tonen; laat ook de openstaande meldingen met rust
        }
        this.hoverTimer.clearAll();
        this.popupOver.set(false);
        this.wisMeldingen();
        if (soort === 'noodzakelijk' || soort === 'gewenst') {
            //de norm- en simballon lezen hun signal rechtstreeks; het bouwsuggestie-overzicht is een
            //rondgang over alle nodes en wordt daarom pas bij het openen opgebouwd
            this.maakBouwsuggestieOverzicht(soort);
        }
        this.hover.set(soort);
    }

    /**
     * De muis verlaat een lampje: sluit de ballon met een kleine vertraging, zodat de gebruiker er
     * naartoe kan bewegen.
     */
    protected muisBuiten(): void {
        this.hoverTimer.set('sluit', () => {
            if (!this.popupOver()) {
                this.hover.set(null);
            }
            this.cdr.markForCheck();
        }, SLUITVERTRAGING);
    }

    /**
     * De muis komt in de ballon zelf: houd hem open.
     */
    protected popupBinnen(): void {
        this.popupOver.set(true);
        this.hoverTimer.clear('sluit');
    }

    /**
     * De muis verlaat de ballon: sluiten.
     */
    protected popupBuiten(): void {
        this.popupOver.set(false);
        this.hover.set(null);
    }

    /**
     * Klik op een lampje: de toggle-uitvoering leeft sinds golf 3c in de `FeedbackbalkService`. De
     * guards daar bepalen of er echt iets gebeurt; wij sturen alleen de klik door.
     */
    protected klikLampje(soort: Soort): void {
        switch (soort) {
            case 'sim':
                this.feedbackbalk.toggleSim();
                break;
            case 'norm':
                this.feedbackbalk.toggleNorm();
                break;
            case 'noodzakelijk':
                this.feedbackbalk.toggleBsNoodzakelijk();
                break;
            case 'gewenst':
                this.feedbackbalk.toggleBsGewenst();
                break;
        }
    }

    /**
     * Klik op een genummerd knopje in de simfeedback-ballon.
     */
    protected klikSimfeedback(type: string, index: number): void {
        this.feedbackSelectie.selecteerSimfeedback(type, index);
    }

    /**
     * Klik op een genummerd knopje in de normfout-ballon.
     */
    protected klikFout(elementId: string, foutcode: string): void {
        this.feedbackSelectie.selecteerFout(elementId, foutcode);
    }

    /**
     * Klik op een genummerd knopje in een bouwsuggestie-ballon.
     */
    protected klikBouwsuggestie(handle: string, nodeId: string, soort: BouwsuggestieSoort): void {
        this.feedbackSelectie.selecteerBouwsuggestie(handle, nodeId, soort);
    }

    /**
     * Klik op de tekstbeschrijving-knop.
     */
    protected klikTekstbeschrijving(): void {
        this.weergaveInstellingen.toggleTekstbeschrijving();
    }

    /**
     * Een klik elders in het scherm wist de "nieuwe dingen"-ballonnen. De hover-ballon blijft
     * bewust staan: die sluit via de mouseleave-handlers, zodat een klik op een genummerd knopje
     * de ballon niet wegtrekt.
     */
    @HostListener('document:mousedown')
    @HostListener('document:touchstart')
    protected opKlikElders(): void {
        this.wisMeldingen();
    }

    // --- Private methods ---

    /**
     * Toont de "nieuwe dingen"-ballon bij een soort. Tijdens het laden van een model gebeurt er niets.
     */
    private toonMelding(soort: Soort): void {
        if (this.feedbackbalk.initLock()) {
            return;
        }
        this.melding.update(huidig => ({...huidig, [soort]: this.translateService.instant('FEEDBACKBUTTONS.NIEUWE_DINGEN')}));
        this.cdr.markForCheck();
    }

    /**
     * Wist alle "nieuwe dingen"-ballonnen.
     */
    private wisMeldingen(): void {
        this.melding.set({sim: null, norm: null, noodzakelijk: null, gewenst: null});
        this.cdr.markForCheck();
    }

    /**
     * Wist de ballonnen. Dat de eerste uitslag na het laden van een model geen melding oplevert
     * regelt de eigenaar van die uitslag: die heeft dan geen voorganger om iets tegen af te zetten.
     */
    private reset(): void {
        this.wisMeldingen();
        this.hover.set(null);
        this.bouwsuggestieOverzicht.set({noodzakelijk: [], gewenst: []});
    }

    /**
     * Volgt per soort of er bij de laatste uitslag iets nieuws bij kwam, en meldt dat als "nieuwe
     * dingen". Sinds de herbouw van de "is nieuw"-bepaling is dat dezelfde vraag over dezelfde lijst
     * als die het icoon bij de node laat pulseren — `some(isNieuw)` — in plaats van een eigen teller
     * die zijn vorige stand bijhoudt.
     *
     * De effecten hangen aan de verzameling zelf en niet aan een boolean. De eigenaren zetten er bij
     * elke uitslag een verse in, ook een lege, zodat het effect precies één keer per uitslag draait.
     * Een boolean zou bij twee uitslagen mét nieuws achter elkaar geen flank geven en de tweede
     * melding dus overslaan.
     */
    private volgTellers(): void {
        effect(() => {
            const nieuw = this.simEngine.nieuweMeldingen();
            if (!this.feedbackbalk.initLock() && nieuw.size > 0) {
                this.toonMelding('sim');
            }
        });

        effect(() => {
            const nieuw = this.normService.nieuweFoutsleutels();
            if (!this.feedbackbalk.initLock() && nieuw.size > 0) {
                this.toonMelding('norm');
            }
        });

        effect(() => {
            const nieuw = this.bouwsuggestieEngine.nieuwsPerSoort();
            const initLock = this.feedbackbalk.initLock();
            if (initLock) {
                return;
            }
            if (nieuw.noodzakelijk) {
                this.toonMelding('noodzakelijk');
            }
            if (nieuw.gewenst) {
                this.toonMelding('gewenst');
            }
        });
    }

    /**
     * Houdt het aantal canvas-elementen bij voor de tekstbeschrijving-knop. De `cy` komt pas na de
     * canvas-init beschikbaar, vandaar de poging bij elke wijzigingscyclus tot hij er is.
     */
    private volgCanvas(): void {
        const cy = this.modelcanvas.getCy();
        if (!cy) {
            const timer = setTimeout(() => this.volgCanvas(), 200);
            this.destroyRef.onDestroy(() => clearTimeout(timer));
            return;
        }
        const bijwerken = (): void => {
            this.aantalElementen.set(cy.elements().size());
            this.cdr.markForCheck();
        };
        cy.on('add remove', bijwerken);
        this.destroyRef.onDestroy(() => cy.off('add remove', bijwerken));
        bijwerken();
    }

    /**
     * Bouwt het bouwsuggestie-overzicht voor een soort: alle regels over alle modelnodes,
     * gegroepeerd op vertaalde titel en alfabetisch gesorteerd.
     */
    private maakBouwsuggestieOverzicht(soort: BouwsuggestieSoort): void {
        const cy = this.modelcanvas.getCy();
        const groepenPerTitel = new Map<string, BouwsuggestieGroep>();
        for (const node of (cy?.nodes('.model').toArray() ?? []) as NodeSingular[]) {
            for (const regelinfo of this.bouwsuggestieEngine.regelgroepenVoor(node)[soort]) {
                const titel = this.titelVoor(regelinfo.handle);
                let groep = groepenPerTitel.get(titel);
                if (!groep) {
                    groep = {titel, instanties: []};
                    groepenPerTitel.set(titel, groep);
                }
                //één knopje per node binnen een groep, ook als twee regels dezelfde titel geven; de
                //handle van de eerste regel is dan de sleutel (zelfde weergave als voorheen)
                if (!groep.instanties.some(instantie => instantie.id === node.id())) {
                    groep.instanties.push({id: node.id(), handle: regelinfo.handle});
                }
            }
        }
        const groepen = [...groepenPerTitel.values()].sort((a, b) => a.titel.localeCompare(b.titel));
        this.bouwsuggestieOverzicht.update(huidig => ({...huidig, [soort]: groepen}));
    }

    /**
     * De vertaalde titel van een bouwsuggestieregel; valt terug op de handle als er geen vertaling is.
     */
    private titelVoor(handle: string): string {
        const sleutel = `BOUWSUGGESTIE.REGEL.${handle.toUpperCase()}.TITEL`;
        const vertaling = this.translateService.instant(sleutel);
        return vertaling && vertaling !== sleutel ? vertaling : handle;
    }
}
